import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { parse, isValid, isBefore, format } from 'date-fns';
import PropTypes from 'prop-types';
import { useOrderMutations } from '@/hooks/useOrderMutations';
import InspectorsFilter from './InspectorsFilter';
import { cn } from '@/lib/utils';

const AddPerformingInspector = ({ orderId }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const intl = useIntl();
  const navigate = useNavigate();
  const { addInspector } = useOrderMutations(orderId);

  const goBack = () => {
    navigate(-1);
  };

  const selectInspectorHandler = () => {
    setErrorMessage(null);
  };

  const onSubmit = async evt => {
    evt.preventDefault();
    const formData = new FormData(document.getElementById('addPerformingInspectorForm'));

    if (formData.get('inspector').length !== 36) {
      setErrorMessage('MESSAGE.INSPECTOR_REQUIRED');
      return;
    }

    if (formData.get('date').length !== 16) {
      setErrorMessage('MESSAGE.DATE_REQUIRED');
      return;
    }

    const rawDate = formData.get('date');
    const date = parse(rawDate, "yyyy-MM-dd'T'HH:mm", new Date());
    const maxDate = new Date();

    if (!isValid(date) || !isBefore(date, maxDate)) {
      setErrorMessage('MESSAGE.INVALID_DATE_AFTER_NOW');
      return;
    }

    const formattedDate = format(date, 'yyyy-MM-dd HH:mm:ss');

    const formValues = {
      public_id: formData.get('inspector'),
      date: formattedDate,
      observation: formData.get('observation'),
    };

    addInspector.mutate(formValues, {
      onSuccess: () => {
        navigate(`/orders/${orderId}`);
      },
      onError: (err) => {
        console.log(err);
        if (err?.response?.data?.error) {
          setErrorMessage(err?.response?.data?.error);
        }
      }
    });
  };

  const busy = addInspector.isPending;


  return (
    <form id="addPerformingInspectorForm" onSubmit={onSubmit} className="min-h-full">
      <div className="mx-[1vw] bg-[#eaeaea] border-2 border-[#dedede] border-solid py-[6vh] flex flex-col items-center">
        <div className="w-full max-w-[83%] flex flex-col gap-6">
          <div className="text-center relative">
            <h2 className="text-[calc(16px+0.4vw)] font-bold mb-0">
              {intl.formatMessage({ id: 'ADD_INSPECTOR' })}
            </h2>
            <h3 className="mt-[0.4em] text-[calc(14px+0.4vw)] leading-relaxed text-gray-700">
              {intl.formatMessage({ id: 'HAS_PERFORMED_INSPECTION_MESSAGE' })}
            </h3>
          </div>

          <div className="p-[10px]">
            <InspectorsFilter handleSelect={selectInspectorHandler} />
          </div>

          <div className="flex flex-col p-[10px]">
            <label htmlFor="date" className="text-[10px] font-bold uppercase mb-[0.3rem] text-gray-700">
              <FormattedMessage id="LABEL.DATE" />
            </label>
            <input 
              type="datetime-local" 
              name="date" 
              id="date" 
              required 
              className="min-h-[38px] p-2 border border-gray-300 rounded focus:border-[#5882C1] focus:ring-1 focus:ring-[#5882C1] outline-none transition-all bg-white"
            />
          </div>

          <div className="flex flex-col p-[10px]">
            <label htmlFor="observation" className="text-[10px] font-bold uppercase mb-[0.3rem] text-gray-700">
              <FormattedMessage id="OBSERVATION" />
            </label>
            <textarea 
              name="observation" 
              id="observation" 
              className="h-[100px] p-2 border border-gray-300 rounded resize-none focus:border-[#5882C1] focus:ring-1 focus:ring-[#5882C1] outline-none transition-all bg-white"
            />
          </div>

          <div className="w-full">
            <hr className="bg-[#dddddd] h-[2px] w-[80%] my-8 mx-auto border-0" />
          </div>

          {!!errorMessage && (
            <div className="text-center text-xs p-2 text-red-600 font-medium">
              <FormattedMessage id={errorMessage} />
            </div>
          )}

          <div className="p-[10px] text-center flex justify-center gap-4">
            <button 
              type="button" 
              onClick={goBack} 
              disabled={busy}
              className="h-[45px] w-[150px] bg-[#b4b4b4] text-white rounded font-bold hover:opacity-90 disabled:opacity-75 disabled:cursor-not-allowed transition-all"
            >
              <FormattedMessage id="BUTTON.CANCEL" />
            </button>
            <button 
              type="submit" 
              disabled={busy}
              className={cn(
                "h-[45px] w-[150px] bg-[#859905] text-white rounded font-bold transition-all relative",
                busy ? "cursor-not-allowed" : "hover:opacity-90"
              )}
            >
              {busy ? (
                <div className="absolute inset-0 flex items-center justify-center bg-[#859905]/75 rounded">
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                <FormattedMessage id="ADD" />
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

AddPerformingInspector.propTypes = {
  orderId: PropTypes.string.isRequired,
};

export default AddPerformingInspector;
